// components/steps/CheckoutStep.jsx
import React, { useState, useMemo } from "react";
import { Form, Input, Button, Divider, Spin, Alert } from "antd";
import { post } from "../../../services/ApiService";
import { useNavigate } from "react-router-dom";

const TranscriptionCheckout = ({
  personalInfo,
  fileList,
  medications,
  shippingAddress,
  setShippingAddress,
  currentStep,
  setCurrentStep,
  editingAddress,
  setEditingAddress,
  deliveryMethod,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [saveError, setSaveError] = useState(null);
  const [isLoading, setIsLoading] = useState(null);
  const totalPrice = useMemo(() => {
    if (!medications || medications.length === 0) return 0;
    return 30 + (medications.length - 1) * 10;
  }, [medications]);

  const handleSaveAddress = async () => {
    try {
      setSaveError(null);
      const values = await form.validateFields();
      setShippingAddress((prev) => ({ ...prev, ...values }));
      setEditingAddress(false);
    } catch (error) {
      setSaveError("Please fix the validation errors before proceeding.");
    }
  };

  const handleSubmitTranscription = async () => {
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("personalInfo", JSON.stringify(personalInfo));
      formData.append("medications", JSON.stringify(medications));
      formData.append("shippingAddress", JSON.stringify(shippingAddress));
      formData.append("deliveryMethod", deliveryMethod);
      fileList.forEach((file, index) => {
        formData.append("files", file); // 'files' should match backend field name
      });
      const response = await post(`api/transcriptions/create`, formData, true);
      setIsLoading(false);
      if (response.success) {
        if (response.sendToCustomer) {
          const url = response.invoiceUrl;
          window.location.href = url;
        } else {
          navigate(`/transcription/success/${response.referenceNumber}`);
        }
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching drugs:", error);
    }
  };

  return (
    <div className="step-content review-step">
      <div className="row">
        {/* LEFT SIDE: Delivery / Collection */}
        <div className="col-lg-7">
          {deliveryMethod === "delivery" && (
            <>
              <div className="info-section mb-4">
                <Alert
                  type="info"
                  showIcon
                  message="Important Information"
                  style={{ borderRadius: "0px" }}
                  description={
                    <>
                      <div className="mt-4">
                        <p>
                          <strong>Transcription Service Fee:</strong>
                        </p>
                        <ul
                          style={{ margin: "8px 0 8px 16px", paddingLeft: 8 }}
                        >
                          <li className="metropill">First medication: £30</li>
                          <li className="metropill ">
                            Additional medications: £10 each
                          </li>
                        </ul>
                      </div>

                      <div>
                        <p>
                          <strong>Medication Costs:</strong>
                        </p>
                        <p className="metropill ">
                          Medication prices are not included in this total. We
                          will send you a separate invoice for your medications
                          once our pharmacy team has confirmed availability and
                          pricing.
                        </p>
                      </div>
                    </>
                  }
                />
              </div>
            </>
          )}

          {deliveryMethod === "clinic" && (
            <>
              <Alert
                type="info"
                showIcon
                message="Collection Information"
                style={{ borderRadius: "0px", marginBottom:'10px' }}
                description={
                  <>
                    <div className="mt-4">
                      <p>
                        <strong>One of our team members will contact you to arrange your
                        in-clinic collection.</strong>
                      </p>
                      <p className="mb-2">
                        Please ensure your contact details are accurate and up to date.
                      </p>
                      <p className="mb-2">
                        You will receive instructions and a suitable time slot.
                      </p>
                      <p className="mb-0">
                        For special requests, let us know in advance to ensure a smooth
                        experience.
                      </p>
                    </div>
                  </>
                }
              />
            </>
          )}

          <div className="delivery-address-checkout mb-4">
            <div className="">
              <div className="head-details mb-3">
                <div className="row">
                  <div className="col-lg-9 col-9">
                    <h4>Description</h4>
                  </div>

                  <div className="col-lg-3 col-3">
                    <h4>Price</h4>
                  </div>
                </div>
              </div>
              <div className="product-body">
                {medications && medications.length > 0 && 
                  <>
                    <div className="row">
                      <div className="col-9">
                        <h4 className="m-0 p-0">
                          Transcription Conversion Fee
                        </h4>
                      </div>
                      <div className="col-3 my-auto">
                        <h4 className="metropill-thin">£{totalPrice}</h4>
                      </div>
                      <div className="col-12">
                        <hr className="" />
                      </div>
                    </div>
                    {(medications || []).map((med, idx) => (
                      <div className="row">
                        <div className="col-9">
                          <h4 className="m-0 p-0">
                            {med.name}
                          </h4>
                        </div>
                        <div className="col-3 my-auto">
                          <h4 className="metropill-thin" title="Price will be provided soon">TBC</h4>
                        </div>
                        <div className="col-12">
                          <hr className="" />
                        </div>
                      </div>
                    ))}

                  <div className="row">
                    <div className="col-6"></div>
                    <div className="col-3">
                      <h4 className="text-end">Total:</h4>
                    </div>
                    <div className="col-3">
                      <h4 className="metropill-thin">£{totalPrice}</h4>
                      <hr className="" />
                    </div>
                  </div>
                  </>
                }
              </div>
              <small className="text-danger">
                {/* Medication invoice will follow once confirmed.<br/> */}
                Medication prices displayed are estimates and may differ from the final invoice.
              </small>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Summary */}
        <div className="col-lg-5">
          <div className="cart_mini_wrap p-2 border">
            <div className="summary-box bg-light p-3 mb-3">
              <h6 className="mb-2">Personal Information</h6>
              <hr />
              <div style={{ paddingLeft: "10px" }}>
                <p className="m-0 p-0">
                  <strong>Name:</strong><br/> 
                  <span style={{fontSize:'11px'}}>{personalInfo.name}</span>
                </p>
                <p className="m-0 p-0">
                  <strong>Email:</strong><br/> 
                  <span style={{fontSize:'11px'}}>{personalInfo.email}</span>
                </p>
                <p className="m-0 p-0">
                  <strong>Phone:</strong><br/> 
                  <span style={{fontSize:'11px'}}>{personalInfo.phone}</span>
                </p>
                <p className="m-0 p-0">
                  <strong>DOB:</strong><br/>
                  <span style={{fontSize:'11px'}}>
                    {personalInfo.dob
                    ? new Date(personalInfo.dob).toLocaleDateString("en-GB")
                    : ""}
                  </span>
                </p>
              </div>
            </div>
            {deliveryMethod === "delivery" &&
              <>
              <Divider />
              <div className="delivery-address-checkout mb-4">
                <div className="d-flex justify-content-between align-items-start">
                  <h6>Delivery Address</h6>
                  {!editingAddress && (
                    <Button
                      type="link"
                      onClick={() => setEditingAddress(true)}
                      className="edit_address_btn"
                    >
                      Edit
                    </Button>
                  )}
                </div>

                {editingAddress ? (
                  <Form
                    form={form}
                    layout="vertical"
                    initialValues={shippingAddress}
                  >
                    <Form.Item
                      name="addressLine1"
                      label="Address Line 1"
                      rules={[
                        {
                          required: true,
                          message: "Please enter address line 1",
                        },
                      ]}
                    >
                      <Input placeholder="e.g. 123 Main St" />
                    </Form.Item>

                    <Form.Item
                      name="addressLine2"
                      label="Address Line 2 (Optional)"
                    >
                      <Input placeholder="e.g. Apt 4B" />
                    </Form.Item>

                    <div className="row">
                      <div className="col-lg-6">
                        <Form.Item
                          name="city"
                          label="City"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your city",
                            },
                          ]}
                        >
                          <Input placeholder="e.g. London" />
                        </Form.Item>
                      </div>
                      <div className="col-lg-6">
                        <Form.Item
                          name="postcode"
                          label="Postcode"
                          rules={[
                            {
                              required: true,
                              message: "Please enter your postcode",
                            },
                            {
                              pattern:
                                /^[A-Z]{1,2}[0-9][A-Z0-9]? ?[0-9][A-Z]{2}$/i,
                              message: "Enter a valid UK postcode",
                            },
                          ]}
                        >
                          <Input placeholder="e.g. W1A 1AA" />
                        </Form.Item>
                      </div>
                    </div>

                    <div className="address-actions mt-3">
                      <Button
                        type="primary"
                        className="next-button btn-sm"
                        onClick={handleSaveAddress}
                      >
                        Save Address
                      </Button>
                    </div>
                  </Form>
                ) : (
                  <div className="del_details_wrap p-2">
                    <p className="m-0 p-0">
                      <strong>Address:</strong><br/> 
                      <span style={{fontSize:'11px'}}>
                        {shippingAddress.addressLine1}{" "}
                        {shippingAddress.addressLine2
                        ? `, ${shippingAddress.addressLine2}`
                        : ""}
                      </span>
                    </p>
                    <p className="m-0 p-0">
                      <strong>City:</strong><br/> 
                      <span style={{fontSize:'11px'}}>{shippingAddress.city}</span>
                    </p>
                    <p className="m-0 p-0">
                      <strong>Postcode:</strong><br/> 
                      <span style={{fontSize:'11px'}}>{shippingAddress.postcode}</span>
                    </p>
                  </div>
                )}
              </div>
              </>
            }

          </div>
        </div>
      </div>

      {/* ACTION BUTTONS */}
      {!(deliveryMethod === "delivery" && editingAddress) && (
        <div className="step-actions mt-4">
          <Button
            onClick={() => {
              setCurrentStep(currentStep - 1);
            }}
            className="prev-button"
            style={{ marginRight: 8 }}
            disabled={isLoading}
          >
            Back
          </Button>
          <Button
            type="primary"
            className="next-button"
            disabled={isLoading}
            onClick={handleSubmitTranscription}
          >
            {isLoading ? (
              <>
                <Spin size="small" /> Please wait
              </>
            ) : (
              "Submit"
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TranscriptionCheckout;
